import React, { useEffect } from 'react';
import { Card, Form, Input, InputNumber, Space, Switch, Typography, message } from 'antd';
import type { QaqcPipeWeldtype } from '../../../../types';
import { insertData, updateData } from '../../../../services/api';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/common/Button';

export type QaqcPipeWeldtypeMode = 'create' | 'edit';

interface Props {
  mode?: QaqcPipeWeldtypeMode;
  initialValues?: Partial<QaqcPipeWeldtype> | null;
  onSaved?: (saved: QaqcPipeWeldtype | any) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

const { Title } = Typography;

const QaqcPipeWeldtypeCreate: React.FC<Props> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<QaqcPipeWeldtype>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        project_id: Number(initialValues.project_id ?? 1),
        weldtype_name: initialValues.weldtype_name,
        weldtype_name_long: initialValues.weldtype_name_long || undefined,
        weldtype_factor: Number(initialValues.weldtype_factor ?? 1),
        weldtype_kind: Number(initialValues.weldtype_kind ?? 1),
        pt_flag: !!initialValues.pt_flag,
        rt_flag: !!initialValues.rt_flag,
        mt_flag: !!initialValues.mt_flag,
        ut_flag: !!initialValues.ut_flag,
      } as any);
    } else {
      form.setFieldsValue({ project_id: 1, weldtype_factor: 1, weldtype_kind: 1, pt_flag: false, rt_flag: false, mt_flag: false, ut_flag: false } as any);
    }
  }, [initialValues]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Omit<QaqcPipeWeldtype, 'id'> = {
        project_id: Number(values.project_id) || 1,
        weldtype_name: String(values.weldtype_name).trim(),
        weldtype_name_long: values.weldtype_name_long ? String(values.weldtype_name_long).trim() : undefined,
        weldtype_factor: Number(values.weldtype_factor),
        weldtype_kind: Number(values.weldtype_kind),
        pt_flag: !!values.pt_flag,
        rt_flag: !!values.rt_flag,
        mt_flag: !!values.mt_flag,
        ut_flag: !!values.ut_flag,
      };

      if (isEdit && (initialValues as any)?.id) {
        await updateData('qaqc_pipe_weldtype', Number((initialValues as any).id), payload, 'id');
        message.success('Weld type updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_weldtype:changed')); } catch {}
        if (onSaved) {
          onSaved(payload as any);
        } else {
          navigate('/qaqc/piping/weldtypes');
          setTimeout(() => {
            try { window.dispatchEvent(new Event('qaqc_pipe_weldtype:changed')); } catch {}
          }, 100);
        }
      } else {
        const resp = await insertData('qaqc_pipe_weldtype', payload);
        message.success('Weld type created');
        try { window.dispatchEvent(new Event('qaqc_pipe_weldtype:changed')); } catch {}
        const newId = (resp && (resp.id || (resp.ids && resp.ids[0]))) ?? undefined;
        const newRec = { ...(payload as any), id: newId };
        if (onSaved) {
          onSaved(newRec);
          form.resetFields();
        } else {
          navigate('/qaqc/piping/weldtypes');
          setTimeout(() => {
            try { window.dispatchEvent(new Event('qaqc_pipe_weldtype:changed')); } catch {}
          }, 100);
        }
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Save failed');
    }
  };

  return (
    <Card
      bordered
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18 }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Weld Type' : (isEdit ? 'Edit Weld Type' : 'Create Weld Type')}
          </span>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space wrap style={{ gap: 12 }}>
            <Form.Item label="Project" name="project_id" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
              <InputNumber min={1} max={999} style={{ width: 120 }} disabled={isEdit || isViewOnly} />
            </Form.Item>
            <Form.Item label="Weldtype" name="weldtype_name" style={{ marginBottom: 0 }} rules={[{ required: true, max: 4 }]}>
              <Input maxLength={4} style={{ width: 140 }} disabled={isEdit || isViewOnly} />
            </Form.Item>
            <Form.Item label="Long Name" name="weldtype_name_long" style={{ marginBottom: 0 }} rules={[{ max: 100 }] }>
              <Input maxLength={100} style={{ width: 320 }} disabled={isViewOnly} placeholder="Descriptive name" />
            </Form.Item>
            <Form.Item label="Factor" name="weldtype_factor" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
              <InputNumber min={0} max={9999} step={0.01} style={{ width: 120 }} disabled={isViewOnly} />
            </Form.Item>
            <Form.Item label="Kind" name="weldtype_kind" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
              <InputNumber min={0} max={9} step={1} style={{ width: 120 }} disabled={isViewOnly} />
            </Form.Item>
          </Space>

          <Space wrap style={{ gap: 24 }}>
            <Form.Item label="PT" name="pt_flag" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch disabled={isViewOnly} />
            </Form.Item>
            <Form.Item label="RT" name="rt_flag" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch disabled={isViewOnly} />
            </Form.Item>
            <Form.Item label="MT" name="mt_flag" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch disabled={isViewOnly} />
            </Form.Item>
            <Form.Item label="UT" name="ut_flag" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch disabled={isViewOnly} />
            </Form.Item>
          </Space>

          {!isViewOnly && (
            <Space>
              <Button action={isEdit ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipeWeldtypeCreate;
